import { useEffect, useState } from "react";

// URLs already fetched in this session. Remounted cards skip the loader entirely.
const loadedUrls = new Set();

// Safety net so a hung request on a slow network never leaves a loader forever.
const DEFAULT_TIMEOUT_MS = 12000;

const areAllLoaded = (urls) => urls.every((u) => loadedUrls.has(u));

/**
 * Preloads a list of image URLs and reports when every one has settled
 * (loaded, errored or timed out). Returns `true` once ready.
 * An empty list is immediately ready.
 */
const useImagesPreload = (urls = [], timeoutMs = DEFAULT_TIMEOUT_MS) => {
  const key = urls.join("|");
  // Track which URL set is ready so a changed list is never reported ready for a frame.
  const [readyKey, setReadyKey] = useState(() => (areAllLoaded(urls) ? key : null));

  useEffect(() => {
    const list = key ? key.split("|") : [];
    if (areAllLoaded(list)) {
      setReadyKey(key);
      return undefined;
    }

    let cancelled = false;
    let pending = list.filter((u) => !loadedUrls.has(u)).length;
    const imgs = [];

    const settle = () => {
      pending -= 1;
      if (pending <= 0 && !cancelled) setReadyKey(key);
    };

    list.forEach((url) => {
      if (loadedUrls.has(url)) return;
      const img = new Image();
      img.decoding = "async";
      img.onload = () => {
        loadedUrls.add(url);
        settle();
      };
      img.onerror = settle;
      img.src = url;
      imgs.push(img);
    });

    const timer = setTimeout(() => {
      if (!cancelled) setReadyKey(key);
    }, timeoutMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      imgs.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [key, timeoutMs]);

  return readyKey === key || areAllLoaded(urls);
};

export default useImagesPreload;
